import { planAnchorSnapshot, type HighlightAnchor } from '../core/page-geometry';
import type { PdfDocument } from './pdf';

/**
 * A highlighted passage, painted from the page it was highlighted on.
 *
 * The comment beside a highlight shows what it is about. For most PDFs the quote does that, but
 * a font embedded with no map from glyphs back to letters gives the browser's selection glyph
 * numbers instead of characters, and the stored quote reads "+RZ PDQ| ZDOQXWV GRHV" for "How
 * many walnuts does". The rectangles are right regardless, so the comment shows the page's own
 * pixels under them instead — which is also what a teacher saw when they made the highlight.
 *
 * Drawn at view time from the document already open, rather than captured when the comment is
 * posted: no image is stored, nothing about the Worker or the schema changes, and every comment
 * that already carries a garbled quote is mended by the same code.
 *
 * The arithmetic is `planAnchorSnapshot`'s. Everything here is a call into pdf.js or the canvas.
 */

/** Paint `anchor` into `canvas`. False when the page could not be had or the paint failed. */
export type DrawPassage = (anchor: HighlightAnchor, canvas: HTMLCanvasElement) => Promise<boolean>;

/** Device pixels across. Twice a comment panel's width, so it is sharp on a retina screen. */
export const SNAPSHOT_WIDTH = 900;

/** The page's highlight colour and strength, so the passage reads as the same mark. */
const HIGHLIGHT = '#f5c542';
const HIGHLIGHT_ALPHA = 0.38;

export function passageDrawer(doc: PdfDocument): DrawPassage {
  return async (anchor, canvas) => {
    try {
      if (anchor.page > doc.numPages) return false;
      const page = await doc.getPage(anchor.page);
      const plan = planAnchorSnapshot(anchor, page.getViewport({ scale: 1 }), SNAPSHOT_WIDTH);
      if (plan === null) return false;

      canvas.width = plan.canvasWidth;
      canvas.height = plan.canvasHeight;
      const viewport = page.getViewport({ scale: plan.scale, offsetX: plan.offsetX, offsetY: plan.offsetY });
      await page.render({ canvas, viewport }).promise;

      const context = canvas.getContext('2d');
      if (context !== null) {
        context.save();
        context.globalAlpha = HIGHLIGHT_ALPHA;
        context.globalCompositeOperation = 'multiply';
        context.fillStyle = HIGHLIGHT;
        for (const rect of plan.rects) context.fillRect(rect.x, rect.y, rect.w, rect.h);
        context.restore();
      }
      return true;
    } catch {
      // A destroyed document, a cancelled paint, a page pdf.js could not read. The caller
      // falls back to the quote, which is what the comment showed before this existed.
      return false;
    }
  };
}
